import { motion } from 'framer-motion'
import { Truck, Clock, Package, RotateCcw, MapPin, Shield } from 'lucide-react'

const shippingOptions = [
  {
    icon: Truck,
    name: 'Standard Shipping',
    price: '$9.95',
    time: '3-5 business days',
    description: 'Free on orders over $75',
  },
  {
    icon: Clock,
    name: 'Express Shipping',
    price: '$14.95',
    time: '1-2 business days',
    description: 'Priority handling',
  },
]

export default function Shipping() {
  return (
    <div className="min-h-screen bg-pp-ivory">
      {/* Hero */}
      <section className="bg-pp-linen py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="font-display text-display-lg text-pp-ink mb-6">
              Shipping & Returns
            </h1>
            <p className="text-pp-stone text-lg leading-relaxed">
              Fast, reliable delivery across Australia with easy returns.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl mx-auto">
            {/* Shipping Options */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-12"
            >
              <h2 className="font-display text-display-sm text-pp-ink mb-6">
                Shipping Options
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {shippingOptions.map((option) => (
                  <div
                    key={option.name}
                    className="bg-white rounded-2xl p-6 border border-pp-sand/50"
                  >
                    <option.icon className="w-8 h-8 text-pp-terracotta mb-4" />
                    <h3 className="font-display text-lg text-pp-ink mb-1">
                      {option.name}
                    </h3>
                    <p className="text-pp-terracotta font-medium mb-2">
                      {option.price} · {option.time}
                    </p>
                    <p className="text-pp-stone text-sm">{option.description}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Shipping Details */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 border border-pp-sand/50 mb-12"
            >
              <h2 className="font-display text-xl text-pp-ink mb-6">
                Shipping Details
              </h2>
              <div className="space-y-6 text-pp-stone">
                <div className="flex items-start gap-4">
                  <Package className="w-5 h-5 text-pp-sage mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-pp-ink mb-1">
                      Order Processing
                    </h3>
                    <p className="text-sm">
                      Orders are processed and dispatched within 1 business day from
                      our Melbourne warehouse. You'll receive a confirmation email
                      with tracking details once shipped.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <MapPin className="w-5 h-5 text-pp-sage mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-pp-ink mb-1">
                      Delivery Areas
                    </h3>
                    <p className="text-sm">
                      We ship Australia-wide and internationally. Metro
                      areas in Australia typically receive orders fastest. International
                      delivery times vary by destination.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Shield className="w-5 h-5 text-pp-sage mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-pp-ink mb-1">
                      Secure Packaging
                    </h3>
                    <p className="text-sm">
                      All orders are carefully packaged to ensure your teethers
                      arrive in perfect condition. We use eco-friendly packaging
                      materials wherever possible.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Returns */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 border border-pp-sand/50"
            >
              <div className="flex items-center gap-3 mb-6">
                <RotateCcw className="w-6 h-6 text-pp-terracotta" />
                <h2 className="font-display text-xl text-pp-ink">
                  Returns & Exchanges
                </h2>
              </div>

              <div className="space-y-6 text-pp-stone">
                <div>
                  <h3 className="font-medium text-pp-ink mb-2">
                    30-Day Satisfaction Guarantee
                  </h3>
                  <p className="text-sm">
                    We want you to love your Petite Palate purchase. If you're not
                    completely satisfied, you can return unused items in their
                    original packaging within 30 days for a full refund.
                  </p>
                </div>

                <div>
                  <h3 className="font-medium text-pp-ink mb-2">How to Return</h3>
                  <ol className="text-sm space-y-2 list-decimal list-inside">
                    <li>
                      Email us at hello@petitepalate.com.au with your order number
                    </li>
                    <li>We'll provide a return shipping label</li>
                    <li>Pack items securely in original packaging</li>
                    <li>Drop off at your nearest post office</li>
                    <li>Refund processed within 5-7 business days of receipt</li>
                  </ol>
                </div>

                <div>
                  <h3 className="font-medium text-pp-ink mb-2">
                    Damaged or Faulty Items
                  </h3>
                  <p className="text-sm">
                    If your order arrives damaged or you receive a faulty product,
                    please contact us within 7 days with photos. We'll arrange a
                    replacement or refund at no extra cost.
                  </p>
                </div>

                <div className="pt-4 border-t border-pp-sand">
                  <h3 className="font-medium text-pp-ink mb-2">
                    Non-Returnable Items
                  </h3>
                  <p className="text-sm">
                    For hygiene reasons, we cannot accept returns on items that have
                    been used, opened from their original packaging, or show signs
                    of wear. Gift cards are also non-refundable.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}
